import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Car, KeyRound, Search, X } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { STATES } from '../config/site';
import { useVehicle } from '../contexts/VehicleContext';
import { cleanPlate, getPlateKey, lookupPlate, setPlateKey, toVehicle, usesProxy, validPlate, vehicleLabel } from '../lib/plate';

/** Registration (rego) + state search. Asks for a PlateAPI key the first time it is needed. */
const RegoSearch = ({ compact = false }) => {
  const navigate = useNavigate();
  const { vehicle, setVehicle, clearVehicle } = useVehicle();
  const [plate, setPlate] = useState('');
  const [state, setState] = useState((vehicle && vehicle.state) || 'NSW');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [keyOpen, setKeyOpen] = useState(false);
  const [keyDraft, setKeyDraft] = useState('');
  const [keyError, setKeyError] = useState('');

  const run = async () => {
    setError('');
    if (!validPlate(plate)) { setError('Enter your registration, for example ABC123.'); return; }
    if (!usesProxy() && !getPlateKey()) { setKeyOpen(true); return; }
    setBusy(true);
    try {
      const data = await lookupPlate(plate, state);
      const v = toVehicle(data, plate, state);
      if (!v.make) throw new Error('No vehicle was found for that registration and state.');
      setVehicle(v);
      navigate('/products?fit=1');
    } catch (e) {
      if (e.code === 'auth') { setPlateKey(''); setKeyError(e.message); setKeyOpen(true); } else setError(e.message || 'Lookup failed. Please try again.');
    } finally { setBusy(false); }
  };
  const submit = (e) => { e.preventDefault(); run(); };
  const saveKey = (e) => {
    e.preventDefault();
    if (!/^[A-Za-z0-9_\-]{12,}$/.test(keyDraft.trim())) { setKeyError('That does not look like an API key. Keys start with pk_live_.'); return; }
    setPlateKey(keyDraft); setKeyDraft(''); setKeyError(''); setKeyOpen(false); setTimeout(run, 0);
  };

  return (
    <div className={compact ? '' : 'bg-white rounded-lg shadow-lg p-4 sm:p-6 max-w-3xl mx-auto text-left'}>
      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3" aria-label="Find parts by registration">
        <div className="flex-1 min-w-0">
          <label htmlFor="rego-plate" className="sr-only">Registration number</label>
          <input
            id="rego-plate"
            value={plate}
            onChange={(e) => setPlate(cleanPlate(e.target.value))}
            placeholder="Registration, e.g. ABC123"
            autoComplete="off"
            autoCapitalize="characters"
            inputMode="text"
            maxLength={8}
            className="w-full h-12 px-4 border border-gray-300 rounded text-lg font-bold tracking-widest uppercase placeholder:font-normal placeholder:tracking-normal placeholder:text-base focus:outline-none focus:border-red-500"
          />
        </div>
        <div className="sm:w-28">
          <label htmlFor="rego-state" className="sr-only">State</label>
          <select id="rego-state" value={state} onChange={(e) => setState(e.target.value)} className="w-full h-12 px-3 border border-gray-300 rounded bg-white focus:outline-none focus:border-red-500">
            {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <button disabled={busy} className="h-12 px-6 bg-red-600 text-white rounded hover:bg-red-700 transition font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
          <Search size={18} />{busy ? 'Looking up…' : 'Find parts'}
        </button>
      </form>
      {error && <p role="alert" className="mt-3 text-sm text-red-600">{error}</p>}
      {vehicle ? (
        <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 rounded bg-gray-50 border border-gray-200 px-3 py-2.5 text-sm">
          <Car size={18} className="text-red-600 shrink-0" />
          <span className="min-w-0"><span className="font-semibold text-gray-900">{vehicleLabel(vehicle)}</span> <span className="text-gray-500">{vehicle.description} · {vehicle.plate} ({vehicle.state})</span></span>
          <button type="button" onClick={clearVehicle} className="ml-auto inline-flex items-center gap-1 text-gray-500 hover:text-red-600"><X size={14} />Clear</button>
        </div>
      ) : (
        <p className="mt-3 text-xs text-gray-500">Enter your number plate and state to see batteries and parts that fit your car.</p>
      )}
      {!usesProxy() && getPlateKey() && (
        <button type="button" onClick={() => { setKeyError(''); setKeyOpen(true); }} className="mt-2 inline-flex items-center gap-1 text-xs text-gray-500 hover:text-red-600"><KeyRound size={12} />Change lookup API key</button>
      )}

      <Dialog open={keyOpen} onOpenChange={setKeyOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Registration lookup API key</DialogTitle>
            <DialogDescription>Looking up a registration uses the PlateAPI service. Enter your API key to continue. It is saved in this browser only and sent only to api.plateapi.com.au.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveKey} className="space-y-3">
            <div>
              <label htmlFor="plate-key" className="block text-sm font-medium text-gray-700 mb-1">API key</label>
              <input id="plate-key" type="password" autoComplete="off" value={keyDraft} onChange={(e) => setKeyDraft(e.target.value)} placeholder="pk_live_…" className="w-full h-10 px-3 border border-gray-300 rounded focus:outline-none focus:border-red-500" />
            </div>
            {keyError && <p role="alert" className="text-sm text-red-600">{keyError}</p>}
            <p className="text-xs text-gray-500">Tip: use the sandbox plate TEST123 (state VIC) to try it out. For a live site, make the lookup from your server so the key is never stored in the browser.</p>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => setKeyOpen(false)} className="h-10 px-4 border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
              <button className="h-10 px-4 bg-red-600 text-white rounded hover:bg-red-700 font-semibold">Save and search</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
export default RegoSearch;
